import hoistNonReactStatic from 'hoist-non-react-statics';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { concat, reduce, isFunction } from 'lodash';
import cookies from 'js-cookie';
import { ABServiceClient } from '@zocdoc/ab';
import { OPTIMIZELY_PROJECT_ID } from './constants';
import { getABParameters } from './selector';
import { sanitizeId } from './util';
import { isProduction } from '../../utils/environment';
import getDisplayName from '../../utils/displayName';
import * as fullstory from '../../utils/fullstory';

const client = new ABServiceClient({
    projectId: OPTIMIZELY_PROJECT_ID,
    fetch,
    basePath: process.env.AWS_API_HOST || '',
});

const getFlags = flagsProvider => {
    return isFunction(flagsProvider) ? flagsProvider() : flagsProvider;
};

export const recordedAssignments = new Set();

export default (flagsProvider, mapAbToProps) => WrappedComponent => {
    const wrappedDisplayName = `ABWrapper(${getDisplayName(WrappedComponent)})`;

    class ABWrapper extends React.Component {
        static propTypes = {
            assignments: PropTypes.object, // TODO: remove this prop and get assignments from global store
        };

        static displayName = wrappedDisplayName;

        componentDidMount() {
            if (document.readyState === 'complete') {
                this.handleRecording();
            } else {
                window.addEventListener('load', this.handleRecording);
            }
        }

        componentWillUnmount() {
            window.removeEventListener('load', this.handleRecording);
        }

        render() {
            const { assignments, ...ownProps } = this.props;
            const abProps = mapAbToProps ? mapAbToProps(assignments, ownProps) : {};
            return (
                <WrappedComponent
                    data-test="ab-wrapped-component"
                    {...this.props}
                    ab={this.props.assignments}
                    {...abProps}
                    recordAssignment={this.recordAssignment}
                />
            );
        }

        handleRecording = () => {
            // convert flags to array
            const flags = concat(getFlags(flagsProvider));

            this.recordAssignments(flags.filter(flag => !flag.recordManually));
        }

        recordAssignment = experiment => {
            this.recordAssignments([experiment]);
        }

        // Note that this method should only be called on the client so we are
        // triggering it in componentDidMount.
        recordAssignments = experiments => {
            // each experiment only needs to be recorded once
            const experimentsToRecord = experiments.filter(experiment => !recordedAssignments.has(experiment));

            // this only runs client-side, so passing hostname to isProduction isn't required
            if (!isProduction() || !experimentsToRecord.length) return;

            const transformedExperiments = experimentsToRecord.map(experiment => ({
                visitorIdType: 'sessionId', // default visitorIdType
                ...experiment,
                // TODO: make patientId available on the front-end and send it
                //       when setting assignments
                sessionId: sanitizeId(cookies.get('bsid')),
                trackingId: sanitizeId(cookies.get('firstTimeVisitor')),
            }));

            client.setAssignmentsWithDefault({
                experiments: transformedExperiments,
                requestOriginUrl: window.location.href,
                userAgent: navigator.userAgent,
            });

            experimentsToRecord.forEach(experiment => recordedAssignments.add(experiment));

            fullstory.setUserVars(experimentsToRecord.reduce((acc, experiment) => {
                acc[experiment.experimentId] = this.props.assignments[experiment.experimentId];
                return acc;
            }, {}));
        }
    }

    hoistNonReactStatic(ABWrapper, WrappedComponent);

    // TODO: remove redundant connect wrapper and access AB assignments in mapStateToProps only
    return connect(state => {
        const assignments = getABParameters(state);
        const flagsArr = [].concat(getFlags(flagsProvider)).filter(Boolean);

        return {
            assignments: {
                ...reduce(flagsArr, (props, flag) => {
                    props[flag.experimentId] = assignments[flag.experimentId]
                        || flag.defaultAssignment; // TODO: maybe map default assignment in fetchAssignments?
                    return props;
                }, {}),
            },
        };
    })(ABWrapper);
};
